import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Link,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  Lock as LockIcon,
  PersonOff as DeactivateIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  AccessTime as TimeIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/Index";
import { useParams, useNavigate } from "react-router-dom";
import type { User } from "./types/User";
import EditUserDialog from "./EditUserDialog";
import apiService from "../../service/Api";
import { toast } from "react-toastify";

interface ModulePermission {
  module: string;
  view: boolean;
  add: boolean;
  edit: boolean;
  description?: string;
}

interface Role {
  id: string;
  name: string;
  description: string;
  checked: boolean;
}

interface RecentActivity {
  action: string;
  time: string;
}

export default function UserProfile() {
  const [tabValue, setTabValue] = useState(0);
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([
    { action: "Logged in", time: "2025-08-04 10:00 AM" },
    { action: "Updated profile", time: "2025-08-03 03:15 PM" },
    { action: "Changed password", time: "2025-08-02 09:45 AM" },
  ]);

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
  };

  const handleEditUser = () => {
    if (userData) {
      setEditDialogOpen(true);
    }
  };

  const handleSaveUser = async (updatedUserData: User) => {
    try {
      const backendData = {
        first_name: updatedUserData.first_name || updatedUserData.name?.split(" ")[0] || "",
        last_name: updatedUserData.last_name || updatedUserData.name?.split(" ").slice(1).join(" ") || "",
        email: updatedUserData.email,
        phone_number: updatedUserData.phone || null,
        groups: updatedUserData.roles || [],
        is_active: updatedUserData.status === "Active",
        user_type: updatedUserData.user_type || "individual",
        role: updatedUserData.role || "client",
        organization: updatedUserData.organization || null,
        is_verified: updatedUserData.is_verified || false,
        country: updatedUserData.country || null,
        user_timezone: updatedUserData.user_timezone || "UTC",
        company: updatedUserData.company || null,
        company_branch: updatedUserData.company_branch || null,
        individual: updatedUserData.individual || null,
        is_2fa_enabled: updatedUserData.is_2fa_enabled || false,
      };
      await apiService.patch(`/api/authentication/users/${id}/`, backendData);
      // Refetch user data to update the profile
      const response = await apiService.get<{
        id: string;
        email: string;
        username: string;
        first_name: string;
        last_name: string;
        phone_number: string | null;
        role: string;
        is_active: boolean;
        is_2fa_enabled: boolean;
        last_login: string | null;
        groups: string[] | null;
        company: string | null;
        user_type: string;
        organization: string | null;
        is_verified: boolean;
        country: string | null;
        user_timezone: string;
        company_branch: string | null;
        individual: string | null;
      }>(`/api/authentication/users/${id}/`);
      const user = response.data;
      setUserData({
        id: user.id,
        email: user.email,
        username: user.username,
        first_name: user.first_name,
        last_name: user.last_name,
        name: `${user.first_name} ${user.last_name}`.trim() || user.username,
        phone: user.phone_number || null,
        department: user.company || user.organization || user.groups?.join(", ") || null,
        roles: user.groups || [],
        status: user.is_active ? "Active" : "Suspended",
        lastLogin: user.last_login,
        avatar: user.first_name?.[0]?.toUpperCase() || user.username[0].toUpperCase(),
        groups: user.groups || [],
        user_type: user.user_type,
        role: user.role,
        organization: user.organization,
        is_verified: user.is_verified,
        country: user.country,
        user_timezone: user.user_timezone,
        company: user.company,
        company_branch: user.company_branch,
        individual: user.individual,
        is_2fa_enabled: user.is_2fa_enabled,
      });
      toast.success("User updated successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (err: any) {
      let errorMessage = "Failed to update user. Please try again.";
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Update user error:", err);
    }
  };

  const handleDeactivateUser = async () => {
    try {
      await apiService.patch(`/api/authentication/users/${id}/`, {
        is_active: userData?.status === "Active" ? false : true,
      });
      setUserData((prev) =>
        prev
          ? {
              ...prev,
              is_active: prev.status === "Active" ? false : true,
              status: prev.status === "Active" ? "Suspended" : "Active",
            }
          : null
      );
      toast.success(`User ${userData?.status === "Active" ? "deactivated" : "activated"} successfully`, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (err: any) {
      let errorMessage = `Failed to ${userData?.status === "Active" ? "deactivate" : "activate"} user. Please try again.`;
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Deactivate/Activate user error:", err);
    }
  };

  const handleResetPassword = () => {
    console.log("Reset password clicked");
    // Implement password reset logic (e.g., navigate to reset page or open modal)
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!id) {
          throw new Error("User ID is required");
        }

        const response = await apiService.get<{
          id: string;
          email: string;
          username: string;
          first_name: string;
          last_name: string;
          phone_number: string | null;
          role: string;
          is_active: boolean;
          is_2fa_enabled: boolean;
          last_login: string | null;
          groups: string[] | null;
          company: string | null;
          user_type: string;
          organization: string | null;
          is_verified: boolean;
          country: string | null;
          user_timezone: string;
          company_branch: string | null;
          individual: string | null;
        }>(`/api/authentication/users/${id}/`);

        const user = response.data;
        const mappedUser: User = {
          id: user.id,
          email: user.email,
          username: user.username,
          first_name: user.first_name,
          last_name: user.last_name,
          name: `${user.first_name} ${user.last_name}`.trim() || user.username,
          phone: user.phone_number || null,
          department: user.company || user.organization || user.groups?.join(", ") || null,
          roles: user.groups || [],
          status: user.is_active ? "Active" : "Suspended",
          lastLogin: user.last_login,
          avatar: user.first_name?.[0]?.toUpperCase() || user.username[0].toUpperCase(),
          groups: user.groups || [],
          user_type: user.user_type,
          role: user.role,
          organization: user.organization,
          is_verified: user.is_verified,
          country: user.country,
          user_timezone: user.user_timezone,
          company: user.company,
          company_branch: user.company_branch,
          individual: user.individual,
          is_2fa_enabled: user.is_2fa_enabled,
        };
        setUserData(mappedUser);
      } catch (err: any) {
        let errorMessage = "Failed to load user data. Please try again.";
        if (err.response?.status === 401) {
          errorMessage = "Unauthorized: Please log in again.";
          navigate("/login");
        } else if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        }
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
        console.error("Fetch user error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [id, navigate]);

  const [roles, setRoles] = useState<Role[]>([
    {
      id: "admin",
      name: "Admin",
      description: "Full system control, manages users, configurations, and sensitive data.",
      checked: false,
    },
    {
      id: "sales",
      name: "Sales",
      description: "Handles bookings, marketing, and customer interactions.",
      checked: false,
    },
    {
      id: "dev",
      name: "Dev",
      description: "Manages technical aspects, including software updates and configurations.",
      checked: false,
    },
    {
      id: "operations",
      name: "Operations",
      description: "Oversees funeral streaming logistics, crew, and equipment management.",
      checked: false,
    },
    {
      id: "finance",
      name: "Finance",
      description: "Manages accounts, payments, insurance, and financial reports.",
      checked: false,
    },
    {
      id: "support",
      name: "Support",
      description: "Provides customer assistance, processes feature requests, and handles FAQs.",
      checked: false,
    },
  ]);

  useEffect(() => {
    if (userData && userData.roles) {
      setRoles((prevRoles) =>
        prevRoles.map((role) => ({
          ...role,
          checked: userData.roles.some((userRole: string) => userRole.toLowerCase() === role.id.toLowerCase()),
        }))
      );
    }
  }, [userData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const currentRoles = roles.filter((role) => role.checked);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error || !userData) {
    return (
      <Box sx={{ p: 3, minHeight: "100vh" }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back to Users
        </Button>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          }
        >
          {error || "User not found"}
        </Alert>
      </Box>
    );
  }

  const isAdmin = userData.roles.some((role: string) => role.toLowerCase() === "admin");

  const getModulePermissions = (): ModulePermission[] => {
    const allModules = [
      {
        module: "Bookings",
        description: "Manage venue bookings and reservations",
      },
      {
        module: "Venues",
        description: "Manage venue information and availability",
      },
      {
        module: "Crew Management",
        description: "Manage crew members and assignments",
      },
      {
        module: "Stream Management",
        description: "Manage live streaming and broadcast settings",
      },
      {
        module: "Gear",
        description: "Manage equipment and inventory",
      },
      {
        module: "Contacts",
        description: "Manage client and vendor contact information",
      },
      {
        module: "Memory Table",
        description: "Manage data storage and memory allocation",
      },
      {
        module: "Sales",
        description: "Manage sales transactions and revenue tracking",
      },
    ];

    if (isAdmin) {
      return allModules.map((module) => ({
        ...module,
        view: true,
        add: true,
        edit: true,
      }));
    } else {
      return allModules.map((module) => {
        const hasPermission = module.module === "Bookings" || module.module === "Venues";
        return {
          ...module,
          view: hasPermission,
          add: hasPermission,
          edit: hasPermission,
        };
      });
    }
  };

  const modulePermissions = getModulePermissions();

  const getStatusColor = (status: string) => {
    return status === "Active" ? "success" : "warning";
  };

  const tabContents = [
    // Tab 0: Overview
    <React.Fragment key="overview">
      <Grid container spacing={3} sx={{ mt: 1, ml: 2, mr: 2, py: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ boxShadow: 0 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Contact Information
              </Typography>
              <List disablePadding>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <EmailIcon color="disabled" />
                  </ListItemIcon>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{userData.email}</Typography>
                  </Box>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PhoneIcon color="disabled" />
                  </ListItemIcon>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1">{userData.phone || "N/A"}</Typography>
                  </Box>
                </ListItem>
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CalendarIcon color="disabled" />
                  </ListItemIcon>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Timezone
                    </Typography>
                    <Typography variant="body1">{userData.user_timezone}</Typography>
                  </Box>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PersonIcon color="disabled" />
                  </ListItemIcon>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Access Level
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                      {(userData.roles || []).map((role: string) => (
                        <Chip
                          key={role}
                          label={role}
                          color="primary"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Recent Activity
              </Typography>
              <List disablePadding>
                {recentActivities.map((activity: RecentActivity, index: number) => (
                  <React.Fragment key={index}>
                    <ListItem disablePadding sx={{ py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <TimeIcon color="disabled" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={activity.action}
                        secondary={activity.time}
                        primaryTypographyProps={{ variant: "body2" }}
                        secondaryTypographyProps={{ variant: "caption" }}
                      />
                    </ListItem>
                    {index < recentActivities.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </React.Fragment>,

    // Tab 1: Roles
    <React.Fragment key="roles">
      <Box sx={{ mt: 1, ml: 2, mr: 2, py: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Role Management
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, mt: 2, mr: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Current Roles:
            </Typography>
            {(userData.roles || []).map((role: string) => (
              <Chip key={role} label={role} variant="outlined" size="small" />
            ))}
          </Box>
        </Box>
        <Grid container spacing={2}>
          {roles.map((role) => (
            <Grid size={{ xs: 12, sm: 6 }} key={role.id}>
              <Paper
                sx={{
                  p: 2,
                  cursor: "default",
                  border: role.checked ? 2 : 1,
                  borderColor: role.checked ? "primary.main" : "divider",
                  backgroundColor: role.checked ? "primary.50" : "grey.50",
                  "&:hover": {
                    bgcolor: role.checked ? "primary.100" : "grey.100",
                  },
                  transition: "all 0.2s ease-in-out",
                  opacity: role.checked ? 1 : 0.7,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Checkbox
                    checked={role.checked}
                    disabled={true}
                    sx={{
                      mt: 0.5,
                      "&.Mui-checked": {
                        color: "primary.main",
                      },
                      "&.Mui-disabled": {
                        color: role.checked ? "primary.main" : "action.disabled",
                      },
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: role.checked ? "bold" : "medium",
                          color: role.checked ? "primary.main" : "text.secondary",
                        }}
                      >
                        {role.name}
                      </Typography>
                      {role.checked && (
                        <Chip
                          label="Assigned"
                          size="small"
                          color="primary"
                          variant="filled"
                          sx={{
                            fontSize: "0.7rem",
                            height: 20,
                            fontWeight: "bold",
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: role.checked ? "text.primary" : "text.secondary",
                      }}
                    >
                      {role.description}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </React.Fragment>,

    // Tab 2: Permissions
    <React.Fragment key="permissions">
      <Box sx={{ mt: 1, ml: 2, mr: 2, py: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Module Permissions
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {(userData.roles || []).map((role: string) => (
              <Chip key={role} label={role} color="primary" size="small" />
            ))}
          </Box>
        </Box>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>
                  Module
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", minWidth: 100 }}
                >
                  View
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", minWidth: 100 }}
                >
                  Add
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", minWidth: 100 }}
                >
                  Edit
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {modulePermissions.map((permission) => (
                <TableRow
                  key={permission.module}
                  sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {permission.module}
                      </Typography>
                      {permission.description && (
                        <Tooltip title={permission.description}>
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            <InfoIcon fontSize="small" color="disabled" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={permission.view}
                      color="success"
                      disabled
                      sx={{
                        "&.Mui-checked": {
                          color: "#724E41",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={permission.add}
                      color="success"
                      disabled
                      sx={{
                        "&.Mui-checked": {
                          color: "#724E41",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={permission.edit}
                      color="success"
                      disabled
                      sx={{
                        "&.Mui-checked": {
                          color: "#724E41",
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ mt: 3, p: 2, backgroundColor: "#f8f9fa", borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> Admin users have full access to all modules.
            General Users have limited permissions to Bookings and Venues only.
          </Typography>
        </Box>
      </Box>
    </React.Fragment>,

    // Tab 3: Activity Logs
    <React.Fragment key="activity_logs">
      <Box sx={{ mt: 1, ml: 2, mr: 2, py: 2 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
          Activity Logs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No activity logs available.
        </Typography>
      </Box>
    </React.Fragment>,
  ];

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box sx={{ mb: 3, backgroundColor: "white", borderRadius: 1 }}>
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid size={{ xs: 12, md: 12 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <Avatar sx={{ width: 80, height: 80, fontSize: "1.5rem" }}>
                  {userData.avatar}
                </Avatar>
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold">
                      {userData.name}
                    </Typography>
                    <Chip
                      label={userData.status}
                      color={getStatusColor(userData.status) as any}
                      size="small"
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    {(userData.roles || []).map((role: string) => (
                      <Chip
                        key={role}
                        label={role}
                        color="primary"
                        size="small"
                      />
                    ))}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "text.secondary",
                    }}
                  >
                    <PersonIcon fontSize="small" />
                    <Typography variant="body2">Also a crew member</Typography>
                  </Box>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Last Login: {userData.lastLogin || "N/A"}
              </Typography>
            </Box>
            <Box
              sx={{
                mt: 2,
                mb: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LinkIcon fontSize="small" color="disabled" />
                <Typography variant="body2" color="text.secondary">
                  Linked to Crew Profile
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {userData.name} ({userData.role === "crew" ? "Crew Member" : "N/A"})
                </Typography>
                {userData.individual && (
                  <Link
                    href={`/crew/${userData.individual}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      ml: 1,
                    }}
                  >
                    View Crew Profile
                    <OpenInNewIcon fontSize="small" />
                  </Link>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleEditUser}
                  startIcon={<EditIcon />}
                >
                  Edit User
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleResetPassword}
                  startIcon={<LockIcon />}
                >
                  Reset Password
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeactivateUser}
                  startIcon={<DeactivateIcon />}
                >
                  {userData.status === "Active" ? "Deactivate" : "Activate"}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Overview" />
          <Tab label="Roles" />
          <Tab label="Permissions" />
          <Tab label="Activity Logs" />
        </Tabs>
      </Box>

      <Box sx={{ backgroundColor: "white" }}>{tabContents[tabValue]}</Box>
      {userData && (
        <EditUserDialog
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          onSave={handleSaveUser}
        />
      )}
    </Box>
  );
}